import React from "react";

import {
  Paginator,
  PaginatorChangeEvent,
  PaginatorPageChangeEvent,
} from "primereact/paginator";

import * as Styled from "./table-paginator-footer.styled";

export interface TablePaginatorFooterProps {
  first: number;
  rows: number;
  totalRecords: number;
  rowsPerPageOptions: number[];
  itemsLabel?: string;
  // eslint-disable-next-line no-unused-vars
  onPageChange(event: PaginatorPageChangeEvent): void;
}

export interface TablePaginationHeaderControlsProps {
  first: number;
  rows: number;
  totalRecords: number;
  rowsPerPageOptions: number[];
  itemsLabel?: string;
  spread?: boolean;
  children?: React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onRowsChange(rows: number): void;
}

interface RowsPerPageTemplateOptions {
  value?: number;
  // eslint-disable-next-line no-unused-vars
  onChange?(event: PaginatorChangeEvent): void;
}

interface PageLinkTemplateOptions {
  page: number;
  currentPage: number;
  totalPages?: number;
  className?: string;
  // eslint-disable-next-line no-unused-vars
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const createPaginatorTemplate = (
  rowsPerPageOptions: number[],
  currentRowsPerPage: number,
  itemsLabel: string,
) => ({
  layout:
    "CurrentPageReport PrevPageLink PageLinks NextPageLink RowsPerPageDropdown",
  CurrentPageReport: (options: {
    first: number;
    last: number;
    totalRecords: number;
  }) => {
    const firstRecord = options.totalRecords === 0 ? 0 : options.first;
    const lastRecord = options.totalRecords === 0 ? 0 : options.last;

    return (
      <Styled.PageReportTitle>
        {firstRecord}-{lastRecord} of {options.totalRecords} {itemsLabel}
      </Styled.PageReportTitle>
    );
  },
  PrevPageLink: (options: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
  }) => (
    <Styled.PaginatorBtn
      type="button"
      onClick={options.onClick}
      disabled={options.disabled}
      aria-label="Previous page"
    >
      <i className="pi pi-chevron-left" aria-hidden="true" />
    </Styled.PaginatorBtn>
  ),
  PageLinks: (options: PageLinkTemplateOptions) => {
    const isCurrentPage = options.page === options.currentPage;

    return (
      <Styled.PaginatorPageButton
        type="button"
        className={options.className}
        onClick={options.onClick}
        aria-current={isCurrentPage ? "page" : undefined}
        $isCurrent={isCurrentPage}
      >
        {options.page + 1}
      </Styled.PaginatorPageButton>
    );
  },
  NextPageLink: (options: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
  }) => (
    <Styled.PaginatorBtn
      type="button"
      onClick={options.onClick}
      disabled={options.disabled}
      aria-label="Next page"
    >
      <i className="pi pi-chevron-right" aria-hidden="true" />
    </Styled.PaginatorBtn>
  ),
  RowsPerPageDropdown: (options: RowsPerPageTemplateOptions) => {
    const dropdownOptions = rowsPerPageOptions.map((value) => ({
      label: String(value),
      value,
    }));

    return (
      <Styled.RowsPerPageWrapper>
        <Styled.RowsPerPageLabel>Rows per page</Styled.RowsPerPageLabel>
        <Styled.RowsPerPage
          value={options.value ?? currentRowsPerPage}
          options={dropdownOptions}
          onChange={options.onChange}
        />
      </Styled.RowsPerPageWrapper>
    );
  },
});

export const TablePaginatorFooter = ({
  first,
  rows,
  totalRecords,
  rowsPerPageOptions,
  itemsLabel = "records",
  onPageChange,
}: TablePaginatorFooterProps) => (
  <Styled.TableFooter>
    <Paginator
      first={first}
      rows={rows}
      totalRecords={totalRecords}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={onPageChange}
      template={createPaginatorTemplate(rowsPerPageOptions, rows, itemsLabel)}
    />
  </Styled.TableFooter>
);

export const TablePaginationHeaderControls = ({
  first,
  rows,
  totalRecords,
  rowsPerPageOptions,
  itemsLabel = "records",
  spread = false,
  children,
  onRowsChange,
}: TablePaginationHeaderControlsProps) => {
  const firstRecord = totalRecords === 0 ? 0 : first + 1;
  const lastRecord = totalRecords === 0 ? 0 : Math.min(first + rows, totalRecords);
  const dropdownOptions = rowsPerPageOptions.map((value) => ({
    label: String(value),
    value,
  }));

  return (
    <Styled.HeaderControls $spread={spread}>
      <Styled.PageReportTitle>
        {firstRecord}-{lastRecord} of {totalRecords} {itemsLabel}
      </Styled.PageReportTitle>
      {spread && <Styled.HeaderSpacer />}
      {children}
      <Styled.RowsPerPageWrapper>
        <Styled.RowsPerPageLabel>Rows per page</Styled.RowsPerPageLabel>
        <Styled.RowsPerPage
          value={rows}
          options={dropdownOptions}
          onChange={(event) => onRowsChange(Number(event.value))}
        />
      </Styled.RowsPerPageWrapper>
    </Styled.HeaderControls>
  );
};

export const TablePaginationHeader = (
  props: TablePaginationHeaderControlsProps,
) => (
  <Styled.TableHeader>
    <TablePaginationHeaderControls {...props} />
  </Styled.TableHeader>
);
